import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  DollarSign, 
  Download, 
  User, 
  LogOut,
  Wallet,
  Settings,
  Home
} from 'lucide-react';

const ClientSidebar = ({ activeSection, onSectionChange, client, onLogout, collapsed, isMobile, permissions }) => {
  const navigate = useNavigate();
  
  let menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'quotes', label: 'Quotes', icon: DollarSign },
    { id: 'documents', label: 'Documents', icon: Download },
    // { id: 'wallet', label: 'Wallet', icon: Wallet },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  if(permissions?.accesseWallet) {
    menuItems.splice(4, 0, { id: 'wallet', label: 'Wallet', icon: Wallet });
  }

  const handleSectionChange = (sectionId) => {
    navigate(`/${sectionId}`);
    // Still call the original callback for any mobile sidebar logic
    if (onSectionChange) {
      onSectionChange(sectionId);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      onLogout();
    }
  };

  const getSidebarClasses = () => {
    let baseClasses = 'fixed left-0 top-0 h-full bg-gray-900 text-white flex flex-col border-r border-gray-800 transition-all duration-300 z-50';
    
    if (isMobile) {
      // Mobile sidebar
      baseClasses += ' w-64';
      if (collapsed) {
        baseClasses += ' -translate-x-full';
      } else {
        baseClasses += ' translate-x-0';
      }
    } else {
      // Desktop sidebar
      if (collapsed) {
        baseClasses += ' w-16';
      } else {
        baseClasses += ' w-64';
      }
    }
    
    return baseClasses;
  };

  return (
    <div className={getSidebarClasses()}>
      {/* Logo/Header */}
      <div className="px-4 py-6 border-b border-gray-800">
        <div className="flex items-center">
          <span className={`font-bold text-lg text-white transition-opacity duration-300 ${
            collapsed && !isMobile ? 'opacity-0 hidden' : 'opacity-100'
          }`}>
            Client Portal
          </span>
          {collapsed && !isMobile && (
            <span className="font-bold text-lg text-white">CP</span>
          )}
        </div>
      </div>

      {/* User Info */}
      <div className={`px-4 py-4 border-b border-gray-800 ${
        collapsed && !isMobile ? 'px-2' : ''
      }`}>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
            <User size={20} className="text-gray-300" />
          </div>
          <div className={`min-w-0 flex-1 transition-opacity duration-300 ${
            collapsed && !isMobile ? 'opacity-0 hidden' : 'opacity-100'
          }`}>
            <div className="text-sm font-medium text-white truncate" title={client?.name}>
              {client?.name}
            </div>
            <div className="text-xs text-gray-400 truncate" title={client?.email}>
              {client?.email}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {menuItems.map(item => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          
          return (
            <div key={item.id}>
              <button
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                  isActive 
                    ? 'bg-blue-600 text-white' 
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                } ${collapsed && !isMobile ? 'justify-center px-2' : ''}`}
                onClick={() => handleSectionChange(item.id)}
                title={collapsed && !isMobile ? item.label : ''}
              >
                <Icon size={20} className="flex-shrink-0" />
                <span className={`ml-3 transition-opacity duration-300 ${
                  collapsed && !isMobile ? 'opacity-0 hidden' : 'opacity-100'
                }`}>
                  {item.label}
                </span>
              </button>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="px-2 py-4 border-t border-gray-800 space-y-2">
        <button 
          className={`w-full flex items-center px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white rounded-md transition-colors duration-200 ${
            collapsed && !isMobile ? 'justify-center px-2' : ''
          }`}
          onClick={handleLogout}
          title={collapsed && !isMobile ? 'Logout' : ''}
        >
          <LogOut size={16} className="flex-shrink-0" />
          <span className={`ml-3 transition-opacity duration-300 ${
            collapsed && !isMobile ? 'opacity-0 hidden' : 'opacity-100'
          }`}>
            Logout
          </span>
        </button>
        <div className={`text-center transition-opacity duration-300 ${
          collapsed && !isMobile ? 'opacity-0 hidden' : 'opacity-100'
        }`}>
          {/* Current year */}
          <small className="text-gray-500">© {2025} KGC Innovations</small>
        </div>
      </div>
    </div>
  );
};

export default ClientSidebar;